import React from "react";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
  useRetrieveProjectTimeSheet,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";

import { TimeSheetFilter } from "@planzone/dto";
import { usePreferences } from "@components/preferences";
import {
  getMonth,
  getCurrentWeek,
  initCursor,
  formatLocalDate,
} from "@pages/sharedTimeSheetUtils";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Controls, TreeToggle } from "@common/treeToggle";
import { Props } from "./types";
import { Navigation } from "./navigation";
import { Month } from "./month";
import { Week } from "./week";
import { Allocations } from "./allocations";
import { Total } from "./total";
import { WeekTimeRecords } from "@scenes/weekTimeRecords";
import { Loader } from "./loader";
import { Tutorial } from "./tutorial";
import { DetailsToggle } from "./detailsToggle";
import { ResourceTimeOverview } from "./resourceTimeOverview";
import { Filters } from "./filters";
import styles from "./styles.scss";

function TimeSheetPage({ project }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const {
    isRequestPending: isTimeRecordsTemplatePending,
    isRequestSuccess: isTimeRecordsTemplateSuccess,
    isRequestFailure: isTimeRecordsTemplateFailure,
    retrieveSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(planzone.uuid, "timeRecord");

  React.useEffect(() => {
    !!planzone.uuid &&
      !isTimeRecordsTemplatePending &&
      !isTimeRecordsTemplateSuccess &&
      !isTimeRecordsTemplateFailure &&
      retrieveSecondaryCriteriaTemplate({
        namespace: planzone.uuid,
        scope: "planzone",
        elementType: "timeRecord",
      });
  }, [planzone.uuid]);
  const [cursor, setCursor] = React.useState<DateTime>(initCursor());
  const month = getMonth(cursor);
  const { week, index: weekIndex } = getCurrentWeek(cursor, month);
  const { state, edit } = usePreferences();
  const { projectTimeSheetFilter } = state;
  const filter = {
    ...projectTimeSheetFilter,
    minDate: formatLocalDate(week[0]),
    maxDate: formatLocalDate(week[week.length - 1]),
  };
  const setFilter = (nextFilter: TimeSheetFilter) =>
    edit({ ...state, projectTimeSheetFilter: nextFilter });
  const {
    isRequestPending,
    isRequestSuccess,
    isDirty,
    isRequestFailure: isTimeSheetFailure,
    canLoadMore,
    timeSheet,
    currentPage,
    retrieveProjectTimeSheet,
  } = useRetrieveProjectTimeSheet(project, filter, 30);

  React.useEffect(() => {
    !isRequestPending &&
      !isTimeSheetFailure &&
      isDirty &&
      retrieveProjectTimeSheet({
        project,
        filter,
        page: 0,
        size: timeSheet.resources.length,
      });
  }, [isDirty, isRequestPending]);

  return (
    <TreeToggle>
      {isRequestSuccess && timeSheet.totalAllocationsCount === 0 ? (
        <div className={styles.timeSheetPage}>
          <Tutorial project={project} />
        </div>
      ) : (
        <div className={styles.timeSheetPage}>
          <Helmet>
            <title>Planzone • {t("timeSheet.title")}</title>
            <meta name="description" content={project.description} />
          </Helmet>
          <div className={styles.main}>
            <div className={styles.filters}>
              <div className={styles.filtersRight}>
                <Navigation week={week} cursor={cursor} setCursor={setCursor} />
                <Filters filter={filter} setFilter={setFilter} />
                <DetailsToggle
                  isToggled={!state.isTimeSheetMinimized}
                  toggle={() =>
                    edit({
                      ...state,
                      isTimeSheetMinimized: !state.isTimeSheetMinimized,
                    })
                  }
                />
              </div>
            </div>
            <div className={styles.content}>
              <div className={styles.header}>
                <div>
                  <Controls />
                </div>
                <div>
                  <Month month={month} weekIndex={weekIndex} />
                  <div className={styles.row}>
                    <Week week={week} />
                    {!state.isTimeSheetMinimized && (
                      <ResourceTimeOverview
                        displayCompletionColumn={
                          settings.showTaskCompletionPercentage
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
              <InfiniteScroll canFetchOnIdle>
                {() => (
                  <>
                    <div className={styles.allocations}>
                      <Allocations
                        week={week}
                        timeSheet={timeSheet}
                        filter={filter}
                        project={project}
                        areDetailsDisplayed={!state.isTimeSheetMinimized}
                        showDeactivatedResources={
                          !state.showDeactivatedResourcesOnTimeSheets
                        }
                        isReloading={isDirty && isRequestPending}
                        showCompletionColumn={
                          settings.showTaskCompletionPercentage
                        }
                      />
                      {isRequestPending && !isDirty && <Loader />}
                      <Flag
                        name="root"
                        isDisabled={isRequestPending || !canLoadMore}
                        retrieveItems={() =>
                          retrieveProjectTimeSheet({
                            project,
                            filter,
                            size: 10,
                            page: currentPage,
                          })
                        }
                      />
                    </div>
                    <div className={styles.footer}>
                      <Total
                        project={project}
                        dates={week}
                        timeSheet={timeSheet}
                        areDetailsDisplayed={!state.isTimeSheetMinimized}
                      />
                    </div>
                    <WeekTimeRecords
                      scope={{ projectUuid: project.uuid }}
                      week={week}
                      validationContext="project"
                    />
                  </>
                )}
              </InfiniteScroll>
            </div>
          </div>
        </div>
      )}
    </TreeToggle>
  );
}

export { TimeSheetPage };
